import { computed, shallowRef } from 'vue';

import { WebPushNotificationKind } from '@shared/api/graphql/gql/graphql';

import {
  getPushNotificationPreferences,
  PushNotificationApiError,
  type PushNotificationPreference,
  updatePushNotificationPreference,
} from '../api/push-notifications-api';

interface PushNotificationPreferencePresentation {
  kind: WebPushNotificationKind;
  title: string;
  description: string;
}

export type PushNotificationPreferenceUpdateOutcome = 'updated' | 'access-denied' | 'error';

const PRESENTATIONS: PushNotificationPreferencePresentation[] = [
  {
    kind: WebPushNotificationKind.BookingCreatedOnline,
    title: 'Новые онлайн-записи',
    description: 'Сообщать, когда клиент самостоятельно создаёт запись.',
  },
  {
    kind: WebPushNotificationKind.MessengerMessageReceived,
    title: 'Сообщения в мессенджере',
    description: 'Сообщать о новых входящих сообщениях клиентов.',
  },
];

const defaultPreferences = (): PushNotificationPreference[] => {
  return PRESENTATIONS.map(({ kind }) => ({ kind, enabled: true, available: null }));
};

export const usePushNotificationPreferences = () => {
  const preferences = shallowRef<PushNotificationPreference[]>(defaultPreferences());
  const isLoading = shallowRef(false);
  const pendingKind = shallowRef<WebPushNotificationKind | null>(null);

  const visiblePreferences = computed(() => {
    return PRESENTATIONS.flatMap((presentation) => {
      const preference = preferences.value.find(({ kind }) => kind === presentation.kind);

      if (preference?.available !== true) {
        return [];
      }

      return [{ ...presentation, enabled: preference.enabled }];
    });
  });

  const load = async (): Promise<boolean> => {
    if (isLoading.value) {
      return false;
    }

    isLoading.value = true;
    try {
      preferences.value = await getPushNotificationPreferences();
      return true;
    } catch {
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const update = async (
    kind: WebPushNotificationKind,
    enabled: boolean,
  ): Promise<PushNotificationPreferenceUpdateOutcome> => {
    if (pendingKind.value !== null) {
      return 'error';
    }

    pendingKind.value = kind;
    try {
      preferences.value = await updatePushNotificationPreference(kind, enabled);
      return 'updated';
    } catch (error) {
      if (!(error instanceof PushNotificationApiError) || error.kind !== 'access-denied') {
        return 'error';
      }

      try {
        preferences.value = await getPushNotificationPreferences();
      } catch {
        console.warn('[push.notifications] Failed to refresh preferences after access denial');
      }

      return 'access-denied';
    } finally {
      pendingKind.value = null;
    }
  };

  return {
    visiblePreferences,
    isLoading,
    pendingKind,
    load,
    update,
  };
};
